import {
  DIRECT_MESSAGES_FETCH_REQUEST,
  DIRECT_MESSAGES_FETCH_SUCCESS,
  DIRECT_MESSAGES_FETCH_FAIL,
  DIRECT_MESSAGES_THREAD_REQUEST,
  DIRECT_MESSAGES_THREAD_SUCCESS,
  DIRECT_MESSAGES_THREAD_FAIL,
  DIRECT_MESSAGES_SEND_SUCCESS,
  DIRECT_MESSAGES_THREAD_NEW,
  DIRECT_MESSAGES_DELETE_SUCCESS,
  DIRECT_MESSAGES_THREAD_CLEAR,
  DIRECT_MESSAGES_UNREAD_COUNT_SUCCESS,
  DIRECT_MESSAGES_EDIT_SUCCESS,
  DM_EDITED,
  DM_READ_RECEIPT,
  DM_REACTION_SUCCESS,
  DM_REACTION_UPDATE,
  DM_TYPING,
  DM_TYPING_CLEAR,
  CONVERSATION_DELETE_SUCCESS,
} from 'mastodon/actions/direct_messages';
import type { ApiDirectMessageJSON } from 'mastodon/api_types/direct_messages';
import { compareId } from 'mastodon/compare_id';

interface DirectMessageState {
  // Inbox: map of accountId -> latest message in thread
  inbox: ApiDirectMessageJSON[];
  inboxLoading: boolean;
  inboxError: string | null;

  // Active thread
  threadAccountId: string | null;
  threadMessages: ApiDirectMessageJSON[];
  threadLoading: boolean;
  threadError: string | null;
  threadHasMore: boolean;

  // Unread badge
  unreadCount: number;

  // Read receipts: other account id -> ISO timestamp when they last read
  dmReadReceipts: Record<string, string>;

  // Typing indicator: accountId of whoever is currently typing in the open thread
  typingAccountId: string | null;

  // Tracks when a conversation was locally deleted (epoch ms). Used to
  // suppress a stale DIRECT_MESSAGES_FETCH_SUCCESS that arrives after an
  // optimistic CONVERSATION_DELETE_SUCCESS (the fetch may have been
  // triggered by a dm_received event that raced with the delete).
  deletedConversations: Record<string, number>;

  // Tracks single messages locally deleted (epoch ms). The gap-safe poll cursor
  // (MED-17) re-fetches a recent overlap window, so an in-flight poll whose
  // server snapshot predates a just-deleted message could otherwise re-add it
  // via THREAD_NEW. Short-TTL tombstone consulted in the THREAD_NEW dedup.
  deletedMessages: Record<string, number>;
}

const initialState: DirectMessageState = {
  inbox: [],
  inboxLoading: false,
  inboxError: null,

  threadAccountId: null,
  threadMessages: [],
  threadLoading: false,
  threadError: null,
  threadHasMore: true,

  unreadCount: 0,

  dmReadReceipts: {},
  typingAccountId: null,
  deletedConversations: {},
  deletedMessages: {},
};

// A just-deleted message stays tombstoned for this long — long enough to outlast
// an in-flight poll's pre-delete server snapshot, short enough to bound memory.
const DELETED_MESSAGE_TTL_MS = 60 * 1000;

const isTombstoned = (
  deleted: Record<string, number>,
  id: string,
  now: number,
) => deleted[id] !== undefined && now - deleted[id] < DELETED_MESSAGE_TTL_MS;

const pruneDeletedMessages = (deleted: Record<string, number>, now: number) => {
  const next: Record<string, number> = {};
  let changed = false;
  for (const [id, ts] of Object.entries(deleted)) {
    if (now - ts < DELETED_MESSAGE_TTL_MS) next[id] = ts;
    else changed = true;
  }
  return changed ? next : deleted;
};

type Action = { type: string; [key: string]: unknown };

const removeFromInbox = (inbox: ApiDirectMessageJSON[], id: string) =>
  inbox.filter((m) => m.id !== id);

const removeFromThread = (messages: ApiDirectMessageJSON[], id: string) =>
  messages.filter((m) => m.id !== id);

// Clear an account's deletion marker once the user re-engages it (opens or
// sends). Without this, the FETCH_SUCCESS stale-restore guard can keep filtering
// a freshly RE-CREATED conversation (its new message's created_at can be at/just
// before the client-clock deletion stamp), making it appear only after refresh.
const clearDeleted = (deleted: Record<string, number>, id: string) => {
  if (deleted[id] === undefined) return deleted;
  const next = { ...deleted };
  delete next[id];
  return next;
};

export const directMessagesReducer = (
  state: DirectMessageState = initialState,
  action: Action,
): DirectMessageState => {
  switch (action.type) {
    case DIRECT_MESSAGES_FETCH_REQUEST:
      return { ...state, inboxLoading: true, inboxError: null };

    case DIRECT_MESSAGES_FETCH_SUCCESS: {
      const deleted = state.deletedConversations;
      return {
        ...state,
        inboxLoading: false,
        inbox: (action.messages as ApiDirectMessageJSON[]).filter((m) => {
          for (const id of [m.sender_account.id, m.recipient_account.id]) {
            const deletedAt = deleted[id];
            if (
              deletedAt !== undefined &&
              new Date(m.created_at).getTime() <= deletedAt
            ) {
              return false;
            }
          }
          return true;
        }),
      };
    }

    case DIRECT_MESSAGES_FETCH_FAIL:
      return {
        ...state,
        inboxLoading: false,
        inboxError: String(action.error),
      };

    case DIRECT_MESSAGES_THREAD_REQUEST:
      return {
        ...state,
        threadLoading: true,
        threadError: null,
        threadAccountId: action.accountId as string,
        // Keep existing messages while loading more
        threadMessages: action.maxId ? state.threadMessages : [],
        threadHasMore: true,
        // Re-engaging this account: drop any stale deletion marker.
        deletedConversations: clearDeleted(
          state.deletedConversations,
          action.accountId as string,
        ),
      };

    case DIRECT_MESSAGES_THREAD_SUCCESS: {
      // Protocol/content split: control-plane rows ride the dedicated
      // protocol endpoint — never let one into the content window, even if
      // served by a stale endpoint or a race. EXCEPTION (MD-h): a
      // sibling_backfill-flagged row is a re-delivered copy of a REAL message
      // the server deliberately admitted to this window (it shares its
      // group_key with a content row) — the skipped sibling's bubble renders
      // it. All other protocol rows keep fresh group_keys and stay out.
      const incoming = (action.messages as ApiDirectMessageJSON[]).filter(
        (m) => !m.protocol || m.sibling_backfill,
      );
      let merged: ApiDirectMessageJSON[];
      if (action.maxId) {
        // Older-page prepend (scroll-up history). Dedupe by LOGICAL identity
        // (group_key ?? id): a boundary row can be re-served (concurrent send
        // mid-fetch) and multi-device fanout gives the same logical message a
        // different row id across the page boundary. The already-loaded copy
        // wins so the rendered window keeps stable row identities.
        const existingLogical = new Set(
          state.threadMessages.map((m) => m.group_key ?? m.id),
        );
        const fresh = incoming.filter(
          (m) => !existingLogical.has(m.group_key ?? m.id),
        );
        merged = [...fresh, ...state.threadMessages];
      } else {
        merged = incoming;
      }
      return {
        ...state,
        threadLoading: false,
        threadMessages: merged,
        threadHasMore: incoming.length >= 30,
      };
    }

    case DIRECT_MESSAGES_THREAD_FAIL:
      return {
        ...state,
        threadLoading: false,
        threadError: String(action.error),
      };

    case DIRECT_MESSAGES_SEND_SUCCESS: {
      const msg = action.message as ApiDirectMessageJSON;
      // A protocol send (ack / resend request / shim) is control-plane
      // traffic: it must not append to the content thread or become the
      // inbox preview row. The protocol endpoint + central processor own it.
      if (msg.protocol) return state;
      // Append to thread if we're in the right thread
      const inThread =
        state.threadAccountId &&
        (msg.recipient_account.id === state.threadAccountId ||
          msg.sender_account.id === state.threadAccountId);

      return {
        ...state,
        // Sending to this account re-creates the conversation: drop its stale
        // deletion marker so FETCH_SUCCESS won't filter it back out.
        deletedConversations: clearDeleted(
          state.deletedConversations,
          msg.recipient_account.id,
        ),
        // The 3s poll can deliver our OWN message before this dispatch (the
        // faster send path made that race real, 2026-08-08 — double bubble
        // until refresh). Replace-if-present: the send's copy carries the
        // authoritative plaintext.
        threadMessages: !inThread
          ? state.threadMessages
          : state.threadMessages.some((m) => m.id === msg.id)
            ? state.threadMessages.map((m) => (m.id === msg.id ? msg : m))
            : [...state.threadMessages, msg],
        // Update or add to inbox
        inbox: [
          msg,
          ...state.inbox.filter((m) => {
            const otherId =
              m.sender_account.id === msg.sender_account.id
                ? m.recipient_account.id
                : m.sender_account.id;
            const newOtherId = msg.recipient_account.id;
            return otherId !== newOtherId;
          }),
        ],
      };
    }

    case DIRECT_MESSAGES_THREAD_NEW: {
      // Polling delivered new messages. Append to thread, dedupe by id.
      // Protocol rows are control-plane (central processor owns them) and
      // must never enter the content window — EXCEPT sibling_backfill-flagged
      // re-delivery copies (MD-h), which the thread endpoint admitted
      // deliberately (see THREAD_SUCCESS).
      const incoming = (action.messages as ApiDirectMessageJSON[]).filter(
        (m) => !m.protocol || m.sibling_backfill,
      );
      const accountId = action.accountId as string;
      if (state.threadAccountId !== accountId || incoming.length === 0) {
        return state;
      }
      const existingIds = new Set(state.threadMessages.map((m) => m.id));
      const now = Date.now();
      // Skip ids already present AND ids just deleted (a stale poll snapshot must
      // not resurrect a locally-deleted message — MED-17 overlap re-fetch).
      const newOnes = incoming.filter(
        (m) =>
          !existingIds.has(m.id) &&
          !isTombstoned(state.deletedMessages, m.id, now),
      );
      if (newOnes.length === 0) return state;
      // Keep the thread id-sorted so a late-arriving lower-id straggler (a
      // near-simultaneous send the gap-safe poll cursor re-fetched, MED-17)
      // renders in order rather than being appended after newer messages.
      return {
        ...state,
        threadMessages: [...state.threadMessages, ...newOnes].sort((a, b) =>
          compareId(a.id, b.id),
        ),
      };
    }

    case DIRECT_MESSAGES_DELETE_SUCCESS: {
      const now = Date.now();
      return {
        ...state,
        inbox: removeFromInbox(state.inbox, action.id as string),
        threadMessages: removeFromThread(
          state.threadMessages,
          action.id as string,
        ),
        // Tombstone the id so a racing in-flight poll cannot re-add it (F2).
        deletedMessages: {
          ...pruneDeletedMessages(state.deletedMessages, now),
          [action.id as string]: now,
        },
      };
    }

    case DIRECT_MESSAGES_THREAD_CLEAR:
      return {
        ...state,
        threadAccountId: null,
        threadMessages: [],
        threadHasMore: true,
      };

    case DIRECT_MESSAGES_UNREAD_COUNT_SUCCESS:
      return { ...state, unreadCount: action.count as number };

    case DM_READ_RECEIPT: {
      const p = action.payload as {
        reader_account_id: string;
        read_at: string;
      };
      return {
        ...state,
        dmReadReceipts: {
          ...state.dmReadReceipts,
          [p.reader_account_id]: p.read_at,
        },
      };
    }

    case DM_REACTION_SUCCESS: {
      const updated = action.message as ApiDirectMessageJSON;
      // Match by group_key first (with id fallback). The API response carries
      // the row id the POST acted on, which may differ from the row this tab
      // is rendering (each device renders a different fanout copy of the same
      // logical message). Mirrors DIRECT_MESSAGES_EDIT_SUCCESS and the
      // DM_REACTION_UPDATE handler.
      return {
        ...state,
        threadMessages: state.threadMessages.map((m) =>
          (updated.group_key && m.group_key === updated.group_key) ||
          m.id === updated.id
            ? { ...m, reactions: updated.reactions }
            : m,
        ),
      };
    }

    case DM_REACTION_UPDATE: {
      const {
        dm_id,
        group_key,
        emoji,
        action: rxAction,
        reactor_account_id,
        currentAccountId,
      } = action.payload as {
        dm_id: string;
        group_key?: string | null;
        emoji: string;
        action: string;
        reactor_account_id: string;
        currentAccountId: string;
      };
      const isMine = reactor_account_id === currentAccountId;
      // A logical DM is N fanout rows sharing a group_key; each device renders a
      // DIFFERENT physical row. Match by group_key so the push lands on whatever
      // row this device rendered (fall back to dm_id for legacy/null group_key).
      const matches = (msg: (typeof state.threadMessages)[number]) =>
        group_key ? msg.group_key === group_key : msg.id === dm_id;
      return {
        ...state,
        threadMessages: state.threadMessages.map((msg) => {
          if (!matches(msg)) return msg;
          const reactions = (msg.reactions ?? []).slice();
          const existing = reactions.find((r) => r.emoji === emoji);
          if (rxAction === 'added') {
            // Idempotent: the originating tab already applied this via
            // DM_REACTION_SUCCESS (me=true) — skip its own echo so we don't
            // double-count. A SIBLING device of the same account has NOT applied
            // it (me=false/absent), so it still lands here. (Previously a blanket
            // `if (isMine) return state` dropped it on every device, so a self
            // reaction never reached the sibling — the reported bug.)
            if (existing) {
              if (isMine && existing.me) return msg;
              return {
                ...msg,
                reactions: reactions.map((r) =>
                  r.emoji === emoji
                    ? { ...r, count: r.count + 1, me: r.me || isMine }
                    : r,
                ),
              };
            }
            return {
              ...msg,
              reactions: [...reactions, { emoji, count: 1, me: isMine }],
            };
          } else {
            if (!existing) return msg;
            // Idempotent removal: our own removal was already applied by
            // DM_REACTION_SUCCESS (me=false) — skip the self echo.
            if (isMine && !existing.me) return msg;
            const newCount = existing.count - 1;
            if (newCount <= 0) {
              return {
                ...msg,
                reactions: reactions.filter((r) => r.emoji !== emoji),
              };
            }
            return {
              ...msg,
              reactions: reactions.map((r) =>
                r.emoji === emoji
                  ? { ...r, count: newCount, me: isMine ? false : r.me }
                  : r,
              ),
            };
          }
        }),
      };
    }

    case DIRECT_MESSAGES_EDIT_SUCCESS: {
      const updated = action.message as ApiDirectMessageJSON;
      const text = action.text as string;
      return {
        ...state,
        threadMessages: state.threadMessages.map((m) =>
          m.group_key === updated.group_key || m.id === updated.id
            ? { ...m, edited_at: updated.edited_at, text }
            : m,
        ),
      };
    }

    case DM_EDITED: {
      const messageId = action.messageId as string;
      const editedAt = action.editedAt as string;
      const groupKey = (action as { groupKey?: string }).groupKey;
      // Match by group_key first (with id fallback): the dm_edited payload
      // carries the SENDER's fanout row id, but each device renders a
      // DIFFERENT physical row of the same logical message — an id-only match
      // silently drops the edit on the recipient (mirrors DM_REACTION_UPDATE).
      return {
        ...state,
        threadMessages: state.threadMessages.map((m) =>
          (groupKey && m.group_key === groupKey) || m.id === messageId
            ? { ...m, edited_at: editedAt }
            : m,
        ),
      };
    }

    case DM_TYPING:
      return { ...state, typingAccountId: action.accountId as string };

    case DM_TYPING_CLEAR:
      return { ...state, typingAccountId: null };

    case CONVERSATION_DELETE_SUCCESS: {
      const otherAccountId = action.otherAccountId as string;
      return {
        ...state,
        deletedConversations: {
          ...state.deletedConversations,
          [otherAccountId]: Date.now(),
        },
        inbox: state.inbox.filter(
          (m) =>
            m.sender_account.id !== otherAccountId &&
            m.recipient_account.id !== otherAccountId,
        ),
        threadMessages:
          state.threadAccountId === otherAccountId ? [] : state.threadMessages,
        threadAccountId:
          state.threadAccountId === otherAccountId
            ? null
            : state.threadAccountId,
        threadHasMore: true,
      };
    }

    default:
      return state;
  }
};
